import { useContext, useEffect } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import PwaInstallationInstructionsAlert from '../../molecules/PwaInstallationInstructionsAlert/PwaInstallationInstructionsAlert'
import { useDigitalResourceRepository } from '../../../repositories/DigitalResource/use-digital-resource.repository'
import DigitalResourcesHomeList from '../../organisms/DigitalResourcesHomeList/DigitalResourcesHomeList'
import SplashScreenGuard from '../../../router/guards/SplashScreenGuard'
import PillarsList from '../../organisms/PillarsList/PillarsList'
import { trackPageView } from '../../../helpers/analytics/google-analytics-utils'
import { AppContext } from '../../../stores/App/AppStore'
import { UserContext } from '../../../stores/User/UserStore'
import { usePillarRepository } from '../../../repositories/Pillar/use-pillar-repository'
import { PaymentOrderReason } from '../../../types/PaymentOrderReason'
import MembershipRepository from '../../../repositories/Membership/membership.repository'
import { MembershipType } from '../../../types/MembershipType'
import { useHomePageRepository } from '../../../repositories/HomePage/use-home-page-repository'
import { useMembershipRepository } from '../../../repositories/Membership/use-membership-repository'
import { useMembershipPlanRepository } from '../../../repositories/MembershipPlan/use-membership-plan-repository'
import { useUserRepository } from '../../../repositories/User/use-user-repository'
import { UPMEContentContext } from '../../../stores/UPMEContent/UPMEContentStore'
import useDealRepository from '../../../repositories/Deal/use-deal-repository'
import { reaches25Within6Months, messagePromptToUnionBaseOnAge } from '../../../helpers/utils/membership'
import DealsHomeList from '../../organisms/DealsHomeList/DealsHomeList'
import { MembershipStatus } from '../../../types/MembershipStatus'
import MembershipCardFloatingButton from '../../atoms/MembershipCardFloatingButton/MembershipCardFloatingButton'
import Prompt from '../../molecules/Prompt/Prompt'
import { Route } from '../../../router'
import { HighlightCarouselItemImageAspectRatio } from "../../molecules/HighlightCarouselItemComponent/HighlightCarouselItemComponent"
import HomeSkeleton from './Home.skeleton'
import MyEventsHomeList from '../../organisms/MyEventsHomeList/MyEventsHomeList'
import HighlightCarousel from '../../molecules/HighlightCarousel/HighlightCarousel'
import { useHomeCarouselRepository } from '../../../repositories/HomeCarousel/use-home-carousel-repository'
import { useMembershipSettingsRepository } from '../../../repositories/MembershipSettings/use-membership-settings-repository'
import { shouldUpdateProfileFromMembershipSetting, shouldUpdateProfileDateFromConfiguration } from '../../../helpers/utils/profile-update'
import { useProgrammeRepository } from '../../../repositories/Programme/use-programme-repository'
import ProgrammesList from '../../organisms/ProgrammesList/ProgrammesList'
import { ProgrammeContext } from '../../../stores/Programme/ProgrammeStore'

import styles from './Home.module.scss'
import ProgrammeApplicationFormsHomeList from '../../organisms/ProgrammeApplicationFormsHomeList/ProgrammeApplicationFormsHomeList'

const DEFAULT_PAGE_SIZE = 3

const HomePage = () => {
    const { state } = useContext(AppContext)
    const { state: upmeContentState } = useContext(UPMEContentContext)
    const { state: userState } = useContext(UserContext)
    const { state: programmeState } = useContext(ProgrammeContext)

    // Azure Requirement #9606
    const isUpmeConversionFeatureEnabled = process.env.REACT_APP_UPME_CONVERSION_FEATURE_FLAG_ENABLED === `true`

    const { t } = useTranslation()
    const navigate = useNavigate()

    const { retrieveProgrammes } = useProgrammeRepository()
    const { retrievePillars } = usePillarRepository()
    const { retrieveHomePage } = useHomePageRepository()
    const { retrieveHomeCarousel } = useHomeCarouselRepository()
    const { retrieveMembership } = useMembershipRepository()
    const { retrieveMembershipSettings } = useMembershipSettingsRepository()
    const { resetMembershipPlan } = useMembershipPlanRepository()
    const { retrieveDeals } = useDealRepository()
    const { retrieveUser } = useUserRepository()
    const { retrieveLimitedDigitalResourcesByCategory } = useDigitalResourceRepository()

    const userMembership = userState.membership?.data
    const profileUpdateDateSetting = userState.membershipSettings?.data?.starterProfileUpdateDate || ``
    const userLastUpdatedDate = userMembership?.lastUpdateByUser || ``

    const shouldDisplayStarterProfileUpdatePrompt = userMembership?.type === MembershipType.STARTER && (
        shouldUpdateProfileFromMembershipSetting(profileUpdateDateSetting, userLastUpdatedDate) || shouldUpdateProfileDateFromConfiguration(userLastUpdatedDate)
    )

    const programmes = programmeState.programmes?.data

    const digitalResources = upmeContentState.limitedDigitalResourceByCategory?.data
    const shouldDisplayUnionMembershipPrompt = reaches25Within6Months(userMembership?.dateOfBirth) && userMembership?.type === MembershipType.STARTER
    const upgradeToUnionPromptMessage = userMembership?.dateOfBirth && messagePromptToUnionBaseOnAge(userMembership?.dateOfBirth, userMembership)

    const homeCarouselItems = state.homeCarouselItems?.data
    const deals = state.deals?.data

    const conditionForRendering = !!userMembership && !!state.pages.home && state.pillars.pillarsArray.length > 0 && (!!homeCarouselItems && homeCarouselItems.length > 0)

    resetMembershipPlan()

    useEffect(() => {
        trackPageView(`Home`)
        retrieveMembership()
        retrieveMembershipSettings()
        retrieveUser()
        displayWelcomeUnionMemberMessageIfRequired()
    }, [])

    useEffect(() => {
        if (userMembership) {
            retrieveHomePage(userMembership.type)
            retrievePillars(userMembership.type)
            retrieveHomeCarousel(userMembership.type)
            retrieveLimitedDigitalResourcesByCategory(userMembership?.type)
            retrieveDeals(userMembership.type, DEFAULT_PAGE_SIZE)
            retrieveProgrammes(userMembership.type)
        }
    }, [userMembership])

    const displayWelcomeUnionMemberMessageIfRequired = () => {
        const membershipRepository = new MembershipRepository()

        if (!membershipRepository.hasDisplayedWelcomeUnionMemberDialog() && userMembership?.type === MembershipType.UPME) {
            navigate(Route.UPME_WELCOME_PAGE)
        }
    }

    const handleComeBackButtonClick = async () => {
        navigate(`/membership/payment-summary/${PaymentOrderReason.SIGNUP}`, { replace: true })
    }

    const handleBecomeUnionMemberPromptClick = () => {
        navigate(Route.UPME_CONVERSION_INTRO)
    }

    const handleUpdateProfilePromptClick = () => {
        navigate(Route.PROFILE_SUMMARY)
    }

    const starterMembershipFab = userMembership?.type === MembershipType.STARTER && <MembershipCardFloatingButton />

    const starterUpdateProfilePrompt = shouldDisplayStarterProfileUpdatePrompt && (
        <Prompt
            className={styles.prompt}
            message={t(`prompt_update_profile`)}
            buttonText={t(`profile_update_button`)}
            variant={`warn`}
            onButtonClick={handleUpdateProfilePromptClick}
        />
    )

    const becomeUnionMemberPrompt = isUpmeConversionFeatureEnabled && shouldDisplayUnionMembershipPrompt &&
        <Prompt
            className={styles.prompt}
            message={upgradeToUnionPromptMessage?.message}
            buttonText={t(`prompt_become_union_member_button`)}
            variant={upgradeToUnionPromptMessage?.variant}
            onButtonClick={handleBecomeUnionMemberPromptClick}
        />

    const displayPromptTypeInOrder = becomeUnionMemberPrompt || starterUpdateProfilePrompt

    return (
        <SplashScreenGuard>
            <HomeSkeleton condition={conditionForRendering} >
                <Box>
                    {displayPromptTypeInOrder}
                    <Box className={styles.container}>
                        <Box className={styles.carouselGridItem}>
                            <HighlightCarousel highlightCarouselItems={homeCarouselItems} aspectRatio={HighlightCarouselItemImageAspectRatio.RECTANGLE} showBackground={true} />
                        </Box>
                        <Box className={styles.programmeApplicationFormsList} mt={2}>
                            <ProgrammeApplicationFormsHomeList />
                        </Box>
                        <Box className={styles.introduction}>
                            <Box>
                                <Typography variant='h5' mb={1}>{state.pages.home.welcomeText}</Typography>
                                <Typography variant='body1' mb={1}>{state.pages.home.description}</Typography>
                            </Box>
                            <Box>
                                {userMembership?.status === MembershipStatus.TERMINATED &&
                                    <Button variant='contained' fullWidth onClick={handleComeBackButtonClick}>{t(`home_page_come_back`)}</Button>
                                }
                            </Box>
                        </Box>
                        <Box className={styles.myEventsList}>
                            <MyEventsHomeList />
                        </Box>
                        <Box mt={2}>
                            <Typography variant='h6' mb={2} >{t(`pillar_section`)}</Typography>
                            <PillarsList pillars={state.pillars.pillarsArray} />
                        </Box>
                        <Box className={styles.dealsList}>
                            <DealsHomeList deals={deals} />
                        </Box>
                        <Box className={styles.programmesList}>
                            <ProgrammesList programmes={programmes} />
                        </Box>
                        <Box className={styles.digitalResourcesList}>
                            <DigitalResourcesHomeList resources={digitalResources} />
                        </Box>
                        {starterMembershipFab}
                    </Box>
                    <PwaInstallationInstructionsAlert forceOpen={false} />
                </Box>
            </HomeSkeleton>
        </SplashScreenGuard>
    )
}

export default HomePage